import React, { useMemo, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'

import { useGlobal } from '../../../stores/useGlobal'

import * as S from './styles'

const MENU_ICONS = {
  dispositivos: 'dispositivos',
  admin: 'admin',
  manutencao: 'manutencao',
  virtueyes: 'icone-olho-ligado',
  financeiro: 'financeiro',
  pedidos: 'pedidos',
  sms: 'sms',
  clientes: 'clientes',
  dashboard: 'dashboard',
  usuarios: 'usuarios',
  automacao: 'automacao',
  relatorios: 'relatorios',
  faturas: 'icone-fatura',
  cadastros: 'cadastros',
  solicitacoes: 'solicitacoes',
  fornecedores: 'fornecedor',
  info: 'informacoes',
  'simcards-virgens': 'simcard-vigem',
  portadoras: 'cell-tower',
  'e-sim': 'e-sim',
  dot: 'dot',
  'dot-fill': 'dot-fill'
}

export default function MenuNavLinks() {
  const [active, setActive] = useState(null)
  const [openedMenus, setOpenedMenus] = useState({})
  const isManualAction = useRef(false)
  const menuRefs = useRef({})
  const didInitialScroll = useRef(false)
  const location = useLocation()
  const history = useHistory()

  let estadoExpansaoNavegacao = useSelector(
    (state) => state.manejadorState.expandirNavegacao
  )

  const menusData = useGlobal((state) => state.menus)

  const formatPath = (path) => {
    if (!path) return ''
    return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/'
  }

  useEffect(() => {
    if (!menusData || menusData.length === 0) return

    if (isManualAction.current) {
      isManualAction.current = false
      return
    }

    const currentPath = formatPath(location.pathname)
    let bestMatch = null
    let bestMatchScore = 0

    const calculateMatchScore = (menuPath, currentPath) => {
      if (!menuPath || !currentPath) return 0

      if (menuPath === currentPath) return 1000

      if (currentPath.startsWith(menuPath)) {
        return menuPath.length
      }

      if (currentPath.endsWith(menuPath)) {
        return menuPath.length * 0.8
      }

      if (currentPath.includes(menuPath)) {
        return menuPath.length * 0.5
      }

      return 0
    }

    const checkMenus = (menus) => {
      for (const menu of menus) {
        const menuPath = formatPath(menu.rota)
        const menuScore = calculateMatchScore(menuPath, currentPath)

        if (menuScore > bestMatchScore) {
          bestMatchScore = menuScore
          bestMatch = {
            id: menu.cdMenu,
            parentId: null,
            grandParentId: null,
            isExact: menuScore === 1000
          }
        }

        if (Array.isArray(menu.filhos) && menu.filhos.length > 0) {
          for (const filho of menu.filhos) {
            const filhoPath = formatPath(filho.rota)
            const filhoScore = calculateMatchScore(filhoPath, currentPath)

            if (filhoScore > bestMatchScore) {
              bestMatchScore = filhoScore
              bestMatch = {
                id: filho.cdMenu,
                parentId: menu.cdMenu,
                grandParentId: null,
                isExact: filhoScore === 1000
              }
            }

            if (Array.isArray(filho.filhos) && filho.filhos.length > 0) {
              for (const neto of filho.filhos) {
                const netoPath = formatPath(neto.rota)
                const netoScore = calculateMatchScore(netoPath, currentPath)

                if (netoScore > bestMatchScore) {
                  bestMatchScore = netoScore
                  bestMatch = {
                    id: neto.cdMenu,
                    parentId: filho.cdMenu,
                    grandParentId: menu.cdMenu,
                    isExact: netoScore === 1000
                  }
                }
              }
            }
          }
        }
      }
    }

    checkMenus(menusData)

    if (bestMatch) {
      setActive((prevActive) => {
        if (prevActive !== bestMatch.id) return bestMatch.id
        return prevActive
      })

      if (bestMatch.grandParentId) {
        setOpenedMenus((prev) => {
          const needsUpdate =
            !prev[bestMatch.grandParentId] || !prev[bestMatch.parentId]
          if (needsUpdate) {
            return {
              [bestMatch.grandParentId]: true,
              [bestMatch.parentId]: true
            }
          }
          return prev
        })
      } else if (bestMatch.parentId) {
        setOpenedMenus((prev) => {
          if (!prev[bestMatch.parentId]) {
            return { [bestMatch.parentId]: true }
          }
          return prev
        })
      }
    } else {
      setActive((prevActive) => {
        if (prevActive !== null) return null
        return prevActive
      })
    }
  }, [location.pathname, menusData])

  useEffect(() => {
    if (!estadoExpansaoNavegacao) {
      isManualAction.current = true
      setOpenedMenus({})
    }
  }, [estadoExpansaoNavegacao])

  useEffect(() => {
    if (didInitialScroll.current) return
    if (active && menuRefs.current[active]) {
      const activeElement = menuRefs.current[active]
      setTimeout(() => {
        //* Encontra o container do scroll
        const container =
          activeElement.closest('.mantine-ScrollArea-viewport') ||
          activeElement.closest('[data-scrollable]') ||
          document.querySelector('.container-menus-navegacao')

        if (container) {
          const containerRect = container.getBoundingClientRect()
          const elementRect = activeElement.getBoundingClientRect()

          //* Calcula a posição do elemento em relação ao container de scroll
          const elementTop =
            elementRect.top - containerRect.top + container.scrollTop
          const elementBottom = elementTop + elementRect.height

          //* Área visível do container
          const visibleTop = container.scrollTop
          const visibleBottom = container.scrollTop + container.clientHeight

          //* Faz scroll se os submenus não estiverem visiveis
          const isFullyVisible =
            elementTop >= visibleTop && elementBottom <= visibleBottom

          if (!isFullyVisible) {
            activeElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest'
            })
          }
        } else {
          activeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          })
        }
      }, 100)
      didInitialScroll.current = true
    }
  }, [active])

  //* Scroll automático quando um menu com subMenu nao cabe na barra
  useEffect(() => {
    const openedMenuIds = Object.keys(openedMenus).filter(
      (id) => openedMenus[id]
    )

    if (openedMenuIds.length > 0) {
      const lastOpenedMenuId = openedMenuIds[openedMenuIds.length - 1]
      const menuElement = menuRefs.current[lastOpenedMenuId]

      if (menuElement && menusData) {
        //* Calcula quantos filhos um menu tem
        const findMenuById = (menus, targetId) => {
          for (const menu of menus) {
            if (menu.cdMenu.toString() === targetId) return menu
            if (menu.filhos) {
              const found = findMenuById(menu.filhos, targetId)
              if (found) return found
            }
          }
          return null
        }

        const menuData = findMenuById(menusData, lastOpenedMenuId)

        if (menuData && menuData.filhos && menuData.filhos.length > 0) {
          setTimeout(() => {
            //* Calcula a altura total dos submenus
            const totalSubmenus = menuData.filhos.reduce((total, filho) => {
              return total + 1 + (filho.filhos ? filho.filhos.length : 0)
            }, 0)

            const container =
              menuElement.closest('.mantine-ScrollArea-viewport') ||
              menuElement.closest('[data-scrollable]') ||
              document.querySelector('.container-menus-navegacao') ||
              document.querySelector('.container-menus-navegacao-1')

            if (container) {
              const menuTop = menuElement.offsetTop
              const estimatedHeight = totalSubmenus * 45 + 70
              const containerHeight = container.clientHeight
              const currentScrollTop = container.scrollTop

              //* Calcula onde o menu expandido vai terminar
              const menuBottomPosition = menuTop + estimatedHeight
              const visibleBottom = currentScrollTop + containerHeight

              //* Faz scroll se o menu expandido não couber na tela atual
              if (menuBottomPosition > visibleBottom) {
                //* Calcula o scroll mínimo necessário para mostrar todo o menu
                const scrollNeeded = menuBottomPosition - visibleBottom
                const newScrollTop = currentScrollTop + scrollNeeded

                container.scrollTo({
                  top: newScrollTop,
                  behavior: 'smooth'
                })
              }
            }
          }, 250)
        }
      }
    }
  }, [openedMenus, menusData])

  const getIcon = (icon) => {
    return MENU_ICONS[icon] || false
  }

  const handleMenuToggle = (menuId, shouldOpen = null) => {
    isManualAction.current = true

    setOpenedMenus((prev) => {
      if (shouldOpen !== null) {
        return shouldOpen ? { [menuId]: true } : { [menuId]: false }
      }

      if (prev[menuId]) {
        return { [menuId]: false }
      }

      return { [menuId]: true }
    })
  }

  const formatRoute = (rota) => {
    if (!rota) return ''
    const cleanRoute = rota.replace(/\/+/g, '/')
    return cleanRoute.startsWith('/') ? cleanRoute : `/${cleanRoute}`
  }

  const handleSubMenuClick = (childId, parentId, rota, menuItem) => {
    isManualAction.current = true
    setOpenedMenus({ [parentId]: true })

    if (rota) {
      if (menuItem?.id === 112) {
        const urlMatch = rota.match(/(https?:\/\/[^\s]+)/i)
        window.open(
          urlMatch ? urlMatch[1] : rota,
          '_blank',
          'noopener,noreferrer'
        )
        return
      }

      setActive(childId)
      const normalizedRoute = formatRoute(rota)
      const currentPath = formatPath(location.pathname)

      if (formatPath(normalizedRoute) !== currentPath) {
        history.push(normalizedRoute)
      }
    }
  }

  const handleSubSubMenuClick = (subSubMenuId, subMenuId, menuId, rota) => {
    isManualAction.current = true
    setActive(subSubMenuId)
    setOpenedMenus({
      [menuId]: true,
      [subMenuId]: true
    })

    if (rota) {
      const normalizedRoute = formatRoute(rota)
      const currentPath = formatPath(location.pathname)

      if (formatPath(normalizedRoute) !== currentPath) {
        history.push(normalizedRoute)
      }
    }
  }

  const handleMenuClick = (parentId, rota) => {
    isManualAction.current = true
    setActive(parentId)

    if (rota) {
      const normalizedRoute = formatRoute(rota)
      const currentPath = formatPath(location.pathname)

      if (formatPath(normalizedRoute) !== currentPath) {
        history.push(normalizedRoute)
      }
    }
  }

  const isChildActive = (menu) => {
    if (!Array.isArray(menu.filhos)) return false

    const hasActiveChild = menu.filhos.some((filho) => filho.cdMenu === active)
    if (hasActiveChild) return true

    return menu.filhos.some((filho) => {
      if (!Array.isArray(filho.filhos)) return false
      return filho.filhos.some((neto) => neto.cdMenu === active)
    })
  }

  const isSubChildActive = (submenu) => {
    if (!Array.isArray(submenu.filhos)) return false
    return submenu.filhos.some((neto) => neto.cdMenu === active)
  }

  const renderNavLinks = useMemo(() => {
    if (!Array.isArray(menusData)) return null

    return menusData.map((menu) => {
      const hasChildren = Array.isArray(menu.filhos) && menu.filhos.length > 0

      if (hasChildren) {
        return (
          <S.NavLinkMenu
            key={menu.cdMenu}
            ref={(el) => {
              if (el) menuRefs.current[menu.cdMenu] = el
            }}
            icon={
              <S.ContainerIcon minimized={!estadoExpansaoNavegacao}>
                <S.Icone
                  nome={getIcon(menu?.icone)}
                  active={active === menu.cdMenu || isChildActive(menu)}
                />
              </S.ContainerIcon>
            }
            label={menu.descricao}
            childrenOffset={0}
            active={active === menu.cdMenu || isChildActive(menu)}
            opened={!!openedMenus[menu.cdMenu]}
            onChange={() => {
              const hasActiveChild = menu.filhos.some((filho) => {
                if (filho.cdMenu === active) return true

                if (Array.isArray(filho.filhos) && filho.filhos.length > 0) {
                  return filho.filhos.some((neto) => neto.cdMenu === active)
                }
                return false
              })
              const isMenuOpen = !!openedMenus[menu.cdMenu]

              if (hasActiveChild) {
                if (!isMenuOpen) {
                  handleMenuToggle(menu.cdMenu)
                  return
                }
                handleMenuToggle(menu.cdMenu)
                return
              }

              handleMenuToggle(menu.cdMenu)
            }}
          >
            {menu.filhos.map((filho) => {
              const hasSubChildren =
                Array.isArray(filho.filhos) && filho.filhos.length > 0

              if (hasSubChildren) {
                return (
                  <S.NavLinkSubMenu
                    key={filho.cdMenu}
                    ref={(el) => {
                      if (el) menuRefs.current[filho.cdMenu] = el
                    }}
                    label={filho.descricao || filho.label}
                    active={active === filho.cdMenu || isSubChildActive(filho)}
                    opened={!!openedMenus[filho.cdMenu]}
                    leftSection={
                      <S.CircleIcon active={active === filho.cdMenu} />
                    }
                    icon={
                      <S.ContainerIconSubMenu
                        minimized={!estadoExpansaoNavegacao}
                      >
                        <S.IconeSubMenu
                          nome={
                            active === filho.cdMenu || isSubChildActive(filho)
                              ? 'dot-fill'
                              : 'dot'
                          }
                          active={
                            active === filho.cdMenu || isSubChildActive(filho)
                          }
                        />
                      </S.ContainerIconSubMenu>
                    }
                    onChange={() => {
                      isManualAction.current = true
                      const isSubMenuOpen = !!openedMenus[filho.cdMenu]

                      setOpenedMenus(() => {
                        if (isSubMenuOpen) {
                          return {
                            [menu.cdMenu]: true,
                            [filho.cdMenu]: false
                          }
                        } else {
                          return {
                            [menu.cdMenu]: true,
                            [filho.cdMenu]: true
                          }
                        }
                      })
                    }}
                  >
                    {filho.filhos.map((neto) => (
                      <S.NavLinkSubMenuNv2
                        key={neto.cdMenu}
                        ref={(el) => {
                          if (el) menuRefs.current[neto.cdMenu] = el
                        }}
                        label={neto.descricao || neto.label}
                        active={active === neto.cdMenu}
                        leftSection={
                          <S.CircleIcon active={active === neto.cdMenu} />
                        }
                        icon={
                          <S.ContainerIconSubMenu
                            minimized={!estadoExpansaoNavegacao}
                          >
                            <S.IconeSubMenu
                              nome={active === neto.cdMenu ? 'dot-fill' : 'dot'}
                              active={active === neto.cdMenu}
                            />
                          </S.ContainerIconSubMenu>
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSubSubMenuClick(
                            neto.cdMenu,
                            filho.cdMenu,
                            menu.cdMenu,
                            neto.rota
                          )
                        }}
                      />
                    ))}
                  </S.NavLinkSubMenu>
                )
              }

              return (
                <S.NavLinkSubMenu
                  key={filho.cdMenu}
                  ref={(el) => {
                    if (el) menuRefs.current[filho.cdMenu] = el
                  }}
                  label={filho.descricao || filho.label}
                  active={active === filho.cdMenu}
                  leftSection={
                    <S.CircleIcon active={active === filho.cdMenu} />
                  }
                  icon={
                    <S.ContainerIconSubMenu
                      minimized={!estadoExpansaoNavegacao}
                    >
                      <S.IconeSubMenu
                        nome={active === filho.cdMenu ? 'dot-fill' : 'dot'}
                        active={active === filho.cdMenu}
                      />
                    </S.ContainerIconSubMenu>
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSubMenuClick(
                      filho.cdMenu,
                      menu.cdMenu,
                      filho.rota,
                      filho
                    )
                  }}
                />
              )
            })}
          </S.NavLinkMenu>
        )
      }

      return (
        <S.NavLinkMenu
          key={menu.cdMenu}
          ref={(el) => {
            if (el) menuRefs.current[menu.cdMenu] = el
          }}
          label={menu.descricao}
          active={active === menu.cdMenu}
          onClick={() => handleMenuClick(menu.cdMenu, menu.rota)}
          icon={
            <S.ContainerIcon minimized={!estadoExpansaoNavegacao}>
              <S.Icone
                nome={getIcon(menu?.icone)}
                active={active === menu.cdMenu}
              />
            </S.ContainerIcon>
          }
        />
      )
    })
  }, [
    menusData,
    active,
    estadoExpansaoNavegacao,
    isChildActive,
    openedMenus,
    isSubChildActive,
    handleSubSubMenuClick,
    handleSubMenuClick,
    handleMenuClick
  ])

  return <S.Box w={250}>{renderNavLinks}</S.Box>
}
